import React from 'react';
import PropTypes from 'prop-types';

import { ActionBtn } from '@meronex/components';
import { App } from '@meronex/app';

import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';

import { useTheme } from '@material-ui/core/styles';
import IHistory from '@meronex/icons/mdc/MdcHistory';
import IChart from '@meronex/icons/hi/HiOutlineChartPie';
import IExport from '@meronex/icons/md/MdFileDownload';
import { toast } from 'react-toastify';
import StoreItemDetails from '../StoreItemDetails';

import Transactions from './Transactions';
import storesMng from '../../../api/storesMng';
import { getServerHttpUrl } from '../../../api/storesMng';
import TopBar from '../../../../views/components/TopHeader';
import { createProgressTracker } from '../../../../../utils/progressTracker';

import { Icons } from '../../../../common/Icons';
import BatchesDialog from './BatchesDialog';
import AnalyticsDialog from '../analysis/AnalyticsDialog';

function AuditItemDialog(props) {
  const theme = useTheme();
  const {
    open,
    onClose,
    item,
    itemProducts,
    onReverseTransaction,
    permissions,
  } = props;

  const transactionRef = React.useRef();

  const [openBatchesDialog, setOpenBatchesDialog] = React.useState(false);
  const [openAnalytics, setOpenAnalytics] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const trackerRef = React.useRef(null);

  React.useEffect(() => {
    return () => {
      if (trackerRef.current) trackerRef.current.cleanup();
    };
  }, []);

  const exportItemHistory = async () => {
    if (exporting || !item) return;
    setExporting(true);
    try {
      const progressId = Math.random().toString(36).substring(2, 15);
      const { server } = App;
      const tracker = createProgressTracker(server);
      trackerRef.current = tracker;

      await tracker.start(progressId, {
        onUpdate: () => {},
        onCompleted: (progressDoc) => {
          tracker.cleanup();
          const rowCount = progressDoc.rowCount || 0;
          if (rowCount === 0) {
            toast.info('No transactions found');
            setExporting(false);
            return;
          }
          const httpUrl = getServerHttpUrl();
          const downloadUrl = `${httpUrl}/api/report-download?id=${progressId}`;
          const link = document.createElement('a');
          link.href = downloadUrl;
          const itemName = (item.type?.name || 'item').replace(/[^a-zA-Z0-9]/g, '-');
          link.download = `audit-trail-${itemName}.csv`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          toast.success(`Exported ${rowCount} transactions`);
          setExporting(false);
        },
        onError: (progressDoc) => {
          tracker.cleanup();
          toast.error(progressDoc.error || 'Export failed');
          setExporting(false);
        },
        onStale: () => {
          tracker.cleanup();
          toast.error('Export timed out');
          setExporting(false);
        },
      });

      // Fire-and-forget — progress tracker handles the download
      storesMng.exportItemAuditTrail({
        itemId: item._id,
        progressId,
      }).catch((e) => {
        tracker.cleanup();
        console.error(e);
        toast.error('Failed to export history');
        setExporting(false);
      });
    } catch (e) {
      console.error(e);
      toast.error('Failed to export history');
      setExporting(false);
    }
  };

  const _onClose = () => {
    onClose();
  };
  const headerHeight = 280;

  return (
    <Dialog
      open={open}
      onClose={_onClose}
      className={'fullScreenMobile'}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: { display: 'flex', flexDirection: 'column', overflow: 'hidden' },
      }}>
      <DialogTitle style={{ flexShrink: 0, padding: '0' }}>
        <TopBar
          title={`Item History`}
          icon={<IHistory size={20} />}
          onClose={_onClose}
        />
      </DialogTitle>
      <div
        style={{
          flexShrink: 0,
          padding: '10px 15px',
        }}>
        <StoreItemDetails
          item={item}
          products={itemProducts}
          permissions={permissions}
          showDetails
          showBatches
          showFactory
          showUpdated
          onGroupsCountClick={() => {
            setOpenBatchesDialog(true);
          }}
        />
        {item.transactionsCount > 0 && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              justifyContent: 'center',
              marginTop: '15px',
              marginBottom: '10px',
            }}>
            <ActionBtn
              style={{
                width: 200,
              }}
              label={
                <span>
                  <IChart /> Analyze
                </span>
              }
              onClick={() => {
                setOpenAnalytics(true);
              }}
            />
            <ActionBtn
              style={{
                width: 200,
              }}
              loading={exporting}
              label={
                <span>
                  <IExport /> Export History
                </span>
              }
              onClick={exportItemHistory}
            />
          </div>
        )}
      </div>
      <div style={{
        flex: 1,
        minHeight: 0,
        borderTop: `1px solid ${theme.palette.type === 'dark' ? 'rgba(255,255,255,0.08)' : '#f1f5f9'}`,
      }}>
        <Transactions
          item={item}
          onReverseTransaction={onReverseTransaction}
          fetchTransactions={async ({ limit, skip }) => {
            const transactions = await storesMng.getItemTransactionsCursor(
              item._id,
              limit,
              skip
            );
            return transactions;
          }}
          ref={transactionRef}
          permissions={permissions}
          height={window.innerHeight - headerHeight}
          hasExpandedItem={() => {}}
        />
      </div>
      {openBatchesDialog && (
        <BatchesDialog
          open={openBatchesDialog}
          item={item}
          onClose={() => {
            setOpenBatchesDialog(false);
          }}
        />
      )}
      {openAnalytics && (
        <AnalyticsDialog
          open={openAnalytics}
          onClose={() => {
            setOpenAnalytics(false);
          }}
          item={item}
        />
      )}
    </Dialog>
  );
}

AuditItemDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  item: PropTypes.object,
  itemProducts: PropTypes.array,
  onReverseTransaction: PropTypes.func,
  permissions: PropTypes.object,
};
AuditItemDialog.defaultProps = {
  open: false,
  onClose: () => {},
};

export default AuditItemDialog;
